import React, {Component} from "react";
import {isCtrlPermitted, isPermitted, isShiftPermitted} from "../../utils/listeners";
import {store} from "../../helper/store";
import {
	deleteAnnotation,
	updateDisplaySetting,
	updateMapState,
	updateZStackLevel
} from "../../redux_actions/maps.state.action";
import {Map, Overlay, View} from "ol";
import Select from "ol/interaction/Select";
import {pointerMove, singleClick} from "ol/events/condition";
import {getCenter} from "ol/extent";
import {ScaleLine} from "ol/control";
import {defaults, MouseWheelZoom, PinchRotate, PinchZoom} from "ol/interaction";
import {
	getMaxZoomOffset,
	getViewLevelsInfo,
	getZoomLevels,
	getZoomRatio,
	getZoomScaleForSlide,
	isImportedSlide,
	isZoomForTiled,
	selectedAnnotationStyle
} from "./utils/map_utils";
import Projection from "ol/proj/Projection";
import {syncViews} from "./utils/splitSyncUtils";
import {notifyError} from "./utils/display.notification";
import {decrementExposure, incrementExposure} from "./utils/exposure_utils";
import {
	AnnotationsConstants,
	componentIds,
	defaultRotationDegree,
	MorphleLogo,
	responsiveLayers
} from "../../utils/const";
import {colorArrayToRGB, convertSettingsToFilter, degreeToRadian, radianToDegree} from "../../utils/utils";
import Layers from "./layers/Layers";
import MagicToolLayer from "./layers/MagicToolLayer";
import {Paper} from "@mui/material";
import SlideName from "./headbar_apps/SlideName";
import PreviewBloodLayer from "./layers/PreviewBloodLayer";
import {patch} from "../../helper/request";
import {error_manager} from "../../helper/error_manager";
import {isOwner} from "./utils/displaySettingsUtils";
import {aclPermissions, hasPermissions} from "../../const/ACLPermissions";
import {Fill, Stroke, Style} from "ol/style";


class BaseSlideMap extends Component {

	constructor(props) {
		super(props);
		this.initMap();
		this.state = {
			shiftScrollCount: 0,
			overlayText: null,
		};
		this.isMobile = document.documentElement.clientWidth <= responsiveLayers.MOBILE_WIDTH_LEVEL &&
			(document.documentElement.clientWidth < document.documentElement.clientHeight);
	}

	componentDidMount() {
		this.initView();
		this.map.setTarget(this.key);
		// Re-publish so apps waiting on slidemap.getTarget() (Tile View after an OSD → OL switch) re-render.
		this.updateState({slidemap: this.map});
		this.overlay.setElement(document.getElementById(`${this.key}-popup`));
		document.addEventListener("keydown", this.keyDownListener, false);
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.keyDownListener, false);
	}

	keyDownListener = (event) => {
		// if (!isPermitted(event)) return;
		switch (event.key) {
			case "Escape":
				this.selectInteraction.getFeatures().clear();
				break;
			case "Delete":
				let selectedFeatures = this.selectInteraction.getFeatures();
				selectedFeatures.forEach(feature => store.dispatch(deleteAnnotation(this.props.id,
					feature.get('annotation'))));
				break;
			default:
				break;
		}
	}

	initMap = () => {
		let {slide} = this.props;
		this.overlay = new Overlay({
			autoPan: true,
			autoPanAnimation: {
				duration: 250
			}
		});
		this.selectInteraction = new Select({
			condition: singleClick,
			wrapX: false,
			hitTolerance: 5,
			style: selectedAnnotationStyle(),
			filter: (feature, layer) => !!layer?.values_.onFeatureSelect,
		});
		this.selectInteraction.on('select', (e) => {
			e.deselected.length > 0 && this.overlay.setPosition(undefined) && this.setState({overlayText: null});
			e.selected.forEach(feature => {
				feature.getProperties().label &&
				this.updateOverlay(getCenter(feature.getGeometry().getExtent()), feature.getProperties().label);
				e.target.featureLayerAssociation_[feature.ol_uid].values_.onFeatureSelect(feature)
			});
		});
		this.hoverInteraction = new Select({
			condition: pointerMove,
			wrapX: false,
			hitTolerance: 5,
			style: new Style({
				stroke: new Stroke({
					color: AnnotationsConstants.SELECTED_ANNOTATION.COLOR_PRIMARY,
					width: AnnotationsConstants.SELECTED_ANNOTATION.WIDTH_PRIMARY
				}),
				fill: new Fill({
					color: '#FFFFFF4D',
				}),
			}),
			filter: (feature, layer) => !!layer?.values_.onFeatureHover,
		});

		let scaleLineControl = new ScaleLine({
			units: 'metric',
			minWidth: 40,
		  });
		scaleLineControl.element.id = componentIds.scaleLineControl;

		this.map = new Map({
			controls: [
				scaleLineControl
			],
			overlays: [this.overlay],
			interactions: defaults({mouseWheelZoom: false, pinchZoom: false}).extend([
				new MouseWheelZoom({
					condition: this.onScroll,
					constrainResolution: true,
				}),
				this.selectInteraction,
				this.hoverInteraction,
				new PinchZoom({
					duration: 200
				}),
				new PinchRotate({
					threshold: 90
				})
			]),
			keyboardEventTarget: document,
			// TODO: [Priyanshu] check this flags for performance
			loadTilesWhileAnimating: true,
			loadTilesWhileInteracting: true,
		});
		let pinchRotate = new PinchRotate({
			threshold: 10,
		});
		pinchRotate.setActive(false);
		this.map.addInteraction(pinchRotate);

		this.map.set('id', this.props.id);
		this.map.addEventListener('moveend', this.updateMapState);
		this.map.on('click', this.props.onClick);
		let zoomLevels = getZoomLevels(slide);
		let zoomScale = getZoomScaleForSlide(slide, zoomLevels);
		// TODO: [Priyanshu] Remove from reducer store - zoomLevels, zoomScale
		this.updateState({zoomLevels, zoomScale, nativeTopLevel: isImportedSlide(slide), slidemap: this.map});
	}

	initView = () => {
		let {x, y, z, digitalZoomStatus, slide} = this.props;
		let imageShape = this.getImageShape();
		let projection = new Projection({
			code: 'MORPHLE',
			units: this.unit,
			extent: [0, 0, imageShape[0], imageShape[1]],
			metersPerUnit: this.metersPerUnit,
			global: true,
			getPointResolution: (resolution) => resolution,
		});
		let resolutions = this.getResolutions();
		let view = new View({
			mapId: this.props.id,
			projection,
			enableRotation: !this.isMobile,
			extent: projection.getExtent(),
			center: (x === -1 || y === -1) ? getCenter(projection.getExtent()) : [x, y],
			zoom: z,
			minZoom: Math.max(0, resolutions.length - 8),   // Allow only 8 zoom levels
			maxZoom: resolutions.length - getMaxZoomOffset(slide, digitalZoomStatus),
			maxResolution: resolutions[0] / getZoomRatio(slide),
			constrainResolution: true,      // force integer zoom levels
			constrainOnlyCenter: true,  // TODO: [Priyanshu] add comment
			smoothExtentConstraint: false,
			rotation: degreeToRadian(this.props.displaySetting?.rotation) || 0
		});
		view.on(['change:center', 'change:resolution', 'change:rotation'], syncViews);
		this.map.setView(view);
		this.map.on('keydown', (event) => {
			if (!isPermitted(event.originalEvent))
				return;
			switch (event.originalEvent.code) {
				case 'KeyZ':
					view.setZoom(view.getZoom() * 2 > view.getMaxZoom() ? view.getMinZoom() : view.getMaxZoom());
					break;
				case 'KeyR':
					view.setRotation((Math.floor(view.getRotation() * 2 / Math.PI) + 1) % 4 * Math.PI / 2);
					break;
				default:
					break;
			}
		});
	}

	updateOverlay = (position, overlayText) => {
		this.overlay.setPosition(position);
		this.setState({overlayText});
	}

	updateState = (mapState) => store.dispatch(updateMapState(this.props.id, mapState));

	updateMapState = () => this.updateState({
		x: this.map.getView().getCenter()[0],
		y: this.map.getView().getCenter()[1],
		z: this.map.getView().getZoom(),
		r: this.map.getView().getRotation(),
	});

	getImageShape = () => {
		let {slide} = this.props;
		return [slide.stitched_tile_width * slide.stitched_x_max, slide.stitched_tile_height * slide.stitched_y_max];
	}

	getResolutions = () => {
		let {slide} = this.props;
		let multiplier = this.unit === 'microns' ? slide.uperpixel : 1;
		return getZoomLevels(slide).map((level) => Math.pow(2, parseInt(level)) * multiplier).reverse();
	}

	onScroll = (e) => {
		if (e.type === "wheel" && (e.originalEvent.ctrlKey || e.originalEvent.metaKey)) {
			e.preventDefault();
			if (!isCtrlPermitted(e.originalEvent)) return true;

			const view = this.map.getView();
			const viewRotation = view.getRotation() || 0;
			const maxAngle = 2 * Math.PI;
			let displaySetting = {
				'rotation': parseInt(radianToDegree(viewRotation)),
			};
			store.dispatch(updateDisplaySetting(this.props.activeMapId, displaySetting));
			if (isOwner(this.props.displaySetting) && hasPermissions([aclPermissions.update_slide_setting])) {
				clearTimeout(this.scrollTimeout);
				this.scrollTimeout = setTimeout(() => {
					patch(`/api/display/settings/${this.props.displaySetting.id}/`, displaySetting).then()
						.catch(error => error_manager(`Failed to update rotation`, error))
				}, 5000);
			}
			const deltaRotation = e.originalEvent.deltaY < 0 ? degreeToRadian(defaultRotationDegree) : -degreeToRadian(defaultRotationDegree);
			const delta = viewRotation <= 0 ? (deltaRotation + maxAngle) : viewRotation > maxAngle ? (deltaRotation - maxAngle) : deltaRotation;
			const rotation = viewRotation + delta;
			view.setRotation(rotation);
			return false;
		}

		if (e.type === "wheel" && e.originalEvent.shiftKey) {
			if (!isShiftPermitted(e.originalEvent)) return true;

			let {slide, zStackLevel, exposure} = this.props;
			if (slide.meta?.takeZStack) {
				let maxZLevel = slide.meta?.takeBidirectionalZStack ?
					(slide.meta?.numZLevels - 1) / 2 : (slide.meta?.numZLevels - 1);
				let minZLevel = slide.meta?.takeBidirectionalZStack ?
					(-1 * ((slide.meta?.numZLevels - 1) / 2)) : 0;
				const onStitchedLayer = !isZoomForTiled(this.map.getView().getZoom(), getViewLevelsInfo(slide))
				if (onStitchedLayer) {
					notifyError("zstackerror", "Z Stack only allowed on higher zoom levels.",
						false, 2500);
				} else if (e.originalEvent.deltaY > 0 && zStackLevel > minZLevel) {
					store.dispatch(updateZStackLevel(this.props.id,
						zStackLevel - 1));
					this.setState({
						shiftScrollCount: this.state.shiftScrollCount + 1,
					});
					this.cacheZoomLevels(true, this.map.getLayers().getArray());
				} else if (e.originalEvent.deltaY < 0 && zStackLevel < maxZLevel) {
					store.dispatch(updateZStackLevel(this.props.id,
						zStackLevel + 1));
					this.setState({
						shiftScrollCount: this.state.shiftScrollCount + 1,
					});
					this.cacheZoomLevels(false, this.map.getLayers().getArray());
				}
			}
			if (slide.meta?.exposure_stack) {
				if (e.originalEvent.deltaY < 0) incrementExposure(slide, exposure);
				else if (e.originalEvent.deltaY > 0) decrementExposure(slide, exposure);
			}
			return false;
		}
		return true;
	}

	render() {
		let borderStyle = this.props.showBorder && {'border-style': 'solid', 'border-color': 'red'};
		return (
			<div>
				<div key={this.key} id={this.key} style={{
					height: this.props.height,
					width: this.props.width,
					backgroundColor: colorArrayToRGB((this.props.slide.background_color)?.split(',').map(Number)),
					filter: convertSettingsToFilter(this.props.displaySetting),
					...borderStyle
				}}>
					{this.props.mapsState && Object.keys(this.props.mapsState).length > 1 ?
						<Paper sx={{
							position: "absolute", zIndex: 2, right: 0, top: 0, borderRadius: 0,
							paddingInlineStart: 4
						}}>
							<SlideName {...this.props}/>
						</Paper> : null
					}
					<img className="morphle-watermark" id={componentIds.morphleWatermark} alt={'logo'}
						 src={MorphleLogo}/>
				</div>
				{<div id={`${this.key}-popup`} className="ol-popup" style={{color: "#000000"}}>
					{this.state.overlayText}
				</div>}
				{<div style={{position: "absolute", display: "none"}}>
					<Layers map={this.map} {...this.props.annotationState} />
					<MagicToolLayer map={this.map}/>
					<PreviewBloodLayer map={this.map} />
				</div>}
			</div>
		);
	}

}

export default BaseSlideMap;
